import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppIcon from './AppIcon';
import { findNavEntryByPath, pickLandingPath } from './layout/navigationModules';
import { getNavConfig, clearNavConfig, NavConfig } from '../api/Navigation/moduleConfig';
import { useSignalRConnection } from '../hooks/useSignalRConnection';
import type { FilteredNavigationState } from '../hooks/useFilteredNavigation';

interface RootState {
  user: {
    isLoggedIn: boolean;
    userId: string;
    company?: { id?: number };
  };
}

interface PageConfigGuardProps {
  children: React.ReactNode;
  /**
   * The already role- and config-filtered navigation, i.e. exactly what the sidebar draws. The guard
   * needs it to choose a redirect target that is REACHABLE — see `resolveEscapePath` below. Passed in
   * rather than re-derived so the guard and the sidebar can never disagree about what is enabled.
   */
  navigation: FilteredNavigationState;
}

// Matches RoleProtectedRoute, so the two guards feel like one behaviour rather than two.
const REDIRECT_DELAY_MS = 2200;

/**
 * The ONE path this guard must never block. `resolveEscapePath` falls back to it when the filtered
 * navigation yields no candidate at all, and unlike the other two candidates it does NOT come from
 * navigation that has already had module and page config applied -- so the invariant documented
 * above is false for this branch alone. See CO-502.
 */
export const selfProfilePath = (userId: string): string => `/profile/${userId}/personal`;

/**
 * Route guard that enforces the Operator's per-company module and page switches.
 *
 * 🔴 WHAT THIS FIXES. The switches were NAVIGATION-ONLY. `getPageConfig()` had exactly two consumers —
 * `useFilteredNavigation` (sidebar) and `usePageTabConfig` (tab strips) — and no route guard read
 * either config: `RoleProtectedRoute` checks role and nothing else. So a page an Operator had "turned
 * off" still rendered in full whenever the sidebar was bypassed: a typed URL, a bookmark, a link in an
 * older email, browser history, or an AI-assistant deep link (`NavigationCatalog` returns routes
 * without consulting company config). Hiding a menu entry was never the same as making a page
 * unreachable, and anything relying on the toggle to withhold a surface was relying on something the
 * code did not do.
 *
 * Mounted ONCE around `MasterLayout`'s outlet rather than per route, so a route added later is covered
 * by default. A guard that must be remembered on every new route is a guard that will be forgotten.
 *
 * ── Three states, never two (same discipline as `RoleProtectedRoute`) ──────────────────────────────
 *   - unresolved — config has not arrived. This is NOT a denial. We render the page, because the
 *                  alternative is a loading flash on every navigation in the app for a switch that is
 *                  off for almost nobody. The config read is shared and cached, and the sidebar
 *                  requests it as the shell mounts, so in practice it is already resolved.
 *   - allowed    — no config row disables this page or its module, OR the URL is not in the registry.
 *   - blocked    — a row explicitly says `isEnabled: false`. Only this state redirects.
 *
 * ── Fails OPEN, deliberately, at every step ───────────────────────────────────────────────────────
 * A URL absent from the registry is allowed: callbacks, redirects and detail views have no switch, and
 * blocking on a switch that does not exist would break working pages. A failed config fetch resolves
 * to `[]`, which reads as all-enabled. An absent row means enabled — matching the server default,
 * where a brand-new company has zero rows and every filter treats that as "show everything".
 *
 * ── Not a security boundary ───────────────────────────────────────────────────────────────────────
 * This withholds a SURFACE, not data. Every endpoint behind these pages keeps its own role and tenant
 * checks, and must: this guard is client-side and a determined caller simply skips it. Do not let a
 * page's switch stand in for an authorization check on its API.
 */
/**
 * 🔴 CO-500. Where a blocked page sends the user.
 *
 * This used to be an unconditional `<Navigate to="/" replace />`, which is a DEAD END whenever the
 * tenant has the `home` module switched off: `home` OWNS `path: '/'` (`navigationModules.ts`), so the
 * guard redirected to the very page it was blocking, blocked it again, and the content area stayed
 * blank forever. `HomeRoute` already has the correct fallback and it never ran, because this guard
 * wraps `MasterLayout`'s outlet and short-circuits before `children`.
 *
 * So resolve the target the way `HomeRoute` does, from the SAME filtered navigation:
 *   - `/` still in the filtered nav -> `/`, the dashboard. The previous behaviour, preserved exactly,
 *                                       for the overwhelmingly common case;
 *   - it is not                      -> the first page of the user's OWN filtered sidebar
 *                                       (`pickLandingPath`, which deliberately excludes `/` so it can
 *                                       never hand back a no-op);
 *   - nothing at all                 -> the user's OWN profile page (`selfProfilePath`), which this
 *                                       guard explicitly exempts so it is always reachable.
 *
 * 🔴 That last branch used to be justified as "the profile page, whose route has no guard". That was
 * true of `RoleProtectedRoute` and FALSE of this guard, which wraps MasterLayout's outlet globally --
 * `findNavEntryByPath` resolves `/profile/<id>/personal` to the `profile` module, so a tenant with
 * that module switched off was stranded on the blocked-page notice with nowhere to go (CO-502). The
 * exemption below makes the sentence true instead of merely assumed.
 *
 * FIRST TEST ASKS ABOUT THE PAGE, NOT THE MODULE, and that distinction is the whole point.
 * `HomeRoute` may key on the MODULE (`m.id === 'home'`) because it only decides what to RENDER. Here
 * the module is the wrong question: `home` also owns `/pending-actions`, so a tenant with the module
 * ON but the `/` PAGE switched off keeps `home` in the filtered list while `/` is still blocked -- and
 * a module-keyed test would send the user straight back into the dead end this function exists to
 * prevent. Prod has 10 `'/'` page rows with 0 disabled today, so that configuration is reachable
 * through the Operator UI but is not yet real anywhere.
 *
 * The invariant that matters: this NEVER returns a path this guard would itself block, because every
 * candidate comes from navigation that has already had module AND page config applied, and both of
 * those filters REMOVE rather than merely reorder.
 */
const resolveEscapePath = (modules: FilteredNavigationState['modules'], userId: string): string => {
  const rootPageSurvived = modules.some(
    m =>
      m.pages.some(pg => pg.path === '/') ||
      (m.subModules ?? []).some(sub => sub.pages.some(pg => pg.path === '/'))
  );
  if (rootPageSurvived) return '/';
  return pickLandingPath(modules) ?? selfProfilePath(userId);
};

export const PageConfigGuard = ({ children, navigation }: PageConfigGuardProps) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const userId = useSelector((state: RootState) => state.user.userId);
  const companyId = useSelector((state: RootState) => state.user.company?.id);
  const { subscribe, subscribeToCompanyGroup } = useSignalRConnection();

  const [config, setConfig] = useState<NavConfig | null>(null);
  // Bumped by ModuleConfigUpdated so an Operator's save reaches open sessions without a re-login,
  // mirroring useFilteredNavigation. Without this, a page switched off mid-session stays reachable.
  const [refetchNonce, setRefetchNonce] = useState(0);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      clearNavConfig();
      setConfig(null);
      return;
    }
    let cancelled = false;
    getNavConfig(userId)
      .then(next => {
        if (!cancelled) setConfig(next);
      })
      .catch(() => {
        // getModuleConfig/getPageConfig already swallow errors; this is belt-and-braces so a
        // rejection can never leave the guard stuck deciding against an un-set config.
        if (!cancelled) setConfig({ modules: [], pages: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId, refetchNonce]);

  useEffect(() => {
    if (!isLoggedIn || !companyId) return;
    subscribeToCompanyGroup(companyId).catch(err => {
      console.error('Failed to subscribe to company group:', err);
    });
    const unsubscribe = subscribe('ModuleConfigUpdated', () => {
      clearNavConfig();
      setRefetchNonce(n => n + 1);
    });
    return () => {
      unsubscribe();
    };
  }, [isLoggedIn, companyId, subscribe, subscribeToCompanyGroup]);

  const entry = findNavEntryByPath(location.pathname);

  // An explicit `isEnabled: false` row is the ONLY thing that blocks. A missing row means enabled.
  // CO-502: the last-resort escape must actually satisfy the invariant `resolveEscapePath` claims.
  // `findNavEntryByPath` resolves `/profile/<id>/personal` to the `profile` MODULE, so a tenant
  // holding an explicit `isEnabled:false` row for `profile` made this guard block its own escape --
  // CO-500's dead end at a new address. Never block a user's OWN profile page. The exemption is
  // keyed on the caller's own userId, so it cannot widen access to anyone else's profile, and
  // `RoleProtectedRoute` still applies on top.
  const isOwnLastResort = Boolean(userId) && location.pathname === selfProfilePath(userId);

  // CO-503: a page inside a SUB-module must also honour a module row keyed on the SUB-module id.
  // `findNavEntryByPath` used to hand back only the PARENT id, so a row on e.g. `impact-performance`
  // matched nothing and `/ai-impact-analysis` stayed reachable with the sub-module switched off
  // (runtime-proven by `G5` + `G5-CTL`, campaign-2026-08-29a). Testing BOTH ids is strictly additive:
  // it can only ever match an EXPLICIT `isEnabled:false` row, so a tenant with no config rows at all
  // — 10 of 20 live tenants (CO-496) — is unaffected and everything still defaults to ENABLED.
  const moduleIds = entry ? [entry.moduleId, ...(entry.subModuleId ? [entry.subModuleId] : [])] : [];

  const blocked = Boolean(
    config &&
      entry &&
      !isOwnLastResort &&
      (config.pages.some(p => p.pagePath === entry.pagePath && !p.isEnabled) ||
        config.modules.some(m => moduleIds.includes(m.moduleId) && !m.isEnabled))
  );

  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (!blocked) {
      setRedirect(false);
      return;
    }
    const timer = setTimeout(() => setRedirect(true), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [blocked, location.pathname]);

  if (!blocked) return <>{children}</>;

  // Redirecting before the filtered navigation has landed would resolve against defaults and move the
  // user twice. Holding the notice is the safe wait: it is already the visible state.
  if (redirect && navigation.loaded) {
    const escape = resolveEscapePath(navigation.modules, userId);
    // Guarding against the CO-500 shape itself: never navigate to the path we are blocking.
    if (escape !== location.pathname) return <Navigate to={escape} replace />;
  }

  return (
    <div role="alert" aria-live="assertive" className="flex flex-1 items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <AppIcon name="EyeOff" size={24} />
        </div>
        <h2 className="text-base font-semibold text-foreground">
          {t('This page is turned off for your company')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Redirecting you to your dashboard…')}
        </p>
        <button
          type="button"
          onClick={() => setRedirect(true)}
          className="mt-1 text-sm font-medium text-primary hover:underline"
        >
          {t('Go to dashboard')}
        </button>
      </div>
    </div>
  );
};

export default PageConfigGuard;
